// useCoolingLane — the force-cooling batch count, ONE read for the wall board
// (FloorMonitor) and Home's cooling line (F16 UF-10), so the two screens can
// never disagree. Entity-driven: every ACTIVE cooling batch is banded against
// its own channel's SLA (FCX-26 M2b).
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { statusTone, statusToneClasses } from '@/lib/statusTone'
import { POLL } from '@/lib/pollTiers'
import { withReadTimeout } from '@/lib/saveTimeout'
import { isOverSla, type CoolingStage, type StageClockRow } from '@/lib/coolingSla'
import { useCoolingSla, type CoolingSlaResolver } from '@/lib/useCoolingSla'
import { useEntityFlowResolver } from '@/lib/entityFlow'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { readState, type ReadState } from '@/lib/readState'

export interface CoolingRow extends StageClockRow { batch_id: string; commodity_id: string | null }

// UF-02: words and tints come from the one cooling_batch status map.
const stageOf = (key: CoolingStage) => {
  const { tone, label } = statusTone('cooling_batch', key)
  return { key, label, tint: statusToneClasses(tone).text }
}
export const COOLING_STAGES: { key: CoolingStage; label: string; tint: string }[] =
  (['STAGING', 'COOLING', 'IN_HOLDING', 'RELEASED'] as const).map(stageOf)

export interface CoolingSummary { byStage: Record<string, number>; overSla: number; total: number }

export function summariseCooling(
  rows: CoolingRow[],
  nowMs: number,
  slaFor: CoolingSlaResolver,
  flowOf: (commodityId: string | null | undefined) => string,
): CoolingSummary {
  const byStage: Record<string, number> = {}
  let overSla = 0
  for (const r of rows) {
    byStage[r.status] = (byStage[r.status] ?? 0) + 1
    if (isOverSla(r, nowMs, slaFor(flowOf(r.commodity_id)))) overSla += 1
  }
  return { byStage, overSla, total: rows.length }
}

const EMPTY: CoolingSummary = { byStage: {}, overSla: 0, total: 0 }

/** `read` is the honest state of the count: only 'usable' may be printed as a
 *  number (a failed or pending read must show a dash, never a false 0).
 *  NOTE: with `enabled: false` it reports 'loading' forever; a caller must not
 *  render the figure unless it enabled the read. */
export function useCoolingLane(opts: { enabled?: boolean } = {}): {
  summary: CoolingSummary; read: ReadState
  /** UF-03: the raw read state, so a caller can ask `staleSince` about it. */
  dataUpdatedAt: number; isError: boolean; isPaused: boolean
} {
  const enabled = opts.enabled ?? true
  const tenantId = useActiveTenantId()
  const q = useQuery<CoolingRow[]>({
    queryKey: ['monitor-cooling', tenantId],
    enabled: enabled && !!tenantId,
    refetchInterval: POLL.live,
    queryFn: async () => {
      // Plain (non-!inner) embed: a batch whose delivery is out of scope keeps
      // its row with a null commodity and falls back to the site's lane.
      const { data, error } = await withReadTimeout(supabase
        .from('cooling_batches')
        .select('batch_id, status, created_at, cool_started_at, cool_ended_at, released_at, delivery:deliveries(commodity_id)')
        .in('status', ['STAGING', 'COOLING', 'IN_HOLDING', 'RELEASED']),
        'useCoolingLane: cooling batches')
      if (error) throw error
      return (data ?? []).map(r => {
        const { delivery, ...rest } = r as typeof r & { delivery: { commodity_id: string | null } | null }
        return { ...rest, commodity_id: delivery?.commodity_id ?? null }
      }) as CoolingRow[]
    },
  })
  const slaFor = useCoolingSla()
  const flowOf = useEntityFlowResolver()
  // A batch crosses its SLA because the CLOCK moved, not because its row
  // changed (the poll hands back the same array), so the memo needs a tick.
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 30_000) * 30_000)
  useEffect(() => {
    const id = setInterval(() => setTick(Math.floor(Date.now() / 30_000) * 30_000), 30_000)
    return () => clearInterval(id)
  }, [])
  const summary = useMemo(
    () => q.data ? summariseCooling(q.data, tick, slaFor, flowOf) : EMPTY,
    [q.data, tick, slaFor, flowOf])
  return {
    summary, read: enabled ? readState(q) : 'loading',
    dataUpdatedAt: q.dataUpdatedAt, isError: q.isError, isPaused: q.isPaused,
  }
}
